import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Wordmark } from "@/components/Logo";
import { toast } from "sonner";
import { homeFor } from "@/components/ProtectedRoute";

export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refreshUser();
      toast.success("Password updated");
      nav(homeFor(user.role));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#09090D]">
      <Card className="w-full max-w-md bg-zinc-950 border-amber-500/20 gold-glow">
        <CardContent className="p-8">
          <Wordmark className="mb-6" />
          <h1 className="font-display text-2xl font-black uppercase mb-1">
            {user.must_change_password ? "Set your password" : "Change password"}
          </h1>
          <p className="text-sm text-zinc-400 mb-6">
            {user.must_change_password
              ? "First-time login requires a new password before you can access the dashboard."
              : "Choose a new, strong password."}
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-amber-400">Current password</Label>
              <Input
                type="password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="current-pw"
                className="bg-black border-amber-500/20 mt-1.5"
              />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-amber-400">New password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="new-pw"
                className="bg-black border-amber-500/20 mt-1.5"
              />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-amber-400">Confirm new password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="confirm-pw"
                className="bg-black border-amber-500/20 mt-1.5"
              />
            </div>
            <Button
              disabled={busy}
              data-testid="change-pw-submit"
              className="w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest hover:brightness-110"
            >
              {busy ? "Saving…" : "Save password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
